import { css } from '@emotion/react';
import { sendInvoiceReceipt } from '@frontend/api-invoices';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import {
  ListField,
  Modal,
  useFormField,
  useModalControl,
  useModalLoadingState,
  useAlert,
} from '@frontend/design-system';
import { useSendRefundReceipt } from '../receipt-data-fetching';
import { BaseReceiptModalProps } from '../ViewReceiptModal';

type SendReceiptModalProps = ReturnType<typeof useModalControl>['modalProps'] & BaseReceiptModalProps;

export const SendReceiptModal = ({ invoice, receiptType, refundId, ...props }: SendReceiptModalProps) => {
  const { t } = useTranslation('payments');
  return (
    <Modal {...props} minWidth={400}>
      <Modal.Header>{t('Send Receipt Email')}</Modal.Header>
      <EmailListBody invoice={invoice} closeModal={props.onClose} receiptType={receiptType} refundId={refundId} />
    </Modal>
  );
};

type EmailListBodyProps = {
  closeModal: () => void;
} & BaseReceiptModalProps;
const EmailListBody = ({ closeModal, invoice, refundId, receiptType }: EmailListBodyProps) => {
  const { t } = useTranslation('payments');
  const user = getUser();
  //FIELDS
  const fieldProps = useFormField({
    type: 'list',
    maxAllowed: 3,
    minRequired: 1,
  });

  //REQUEST
  const alert = useAlert();
  const { setLoading } = useModalLoadingState();

  //REFUND RECEIPT
  const { sendRefundReceipt } = useSendRefundReceipt({
    locationId: invoice.locationId,
    paymentId: invoice.payment?.paymentId,
    externalId: refundId,
    userId: user?.userID,
    invoiceId: invoice.id,
  });

  const sendReceipts = async () => {
    try {
      setLoading(true);
      if (receiptType === 'refund') {
        await Promise.all(fieldProps.value.map((email) => sendRefundReceipt(email)));
      } else {
        await sendInvoiceReceipt({ invoiceId: invoice.id, emails: fieldProps.value, userId: user?.userID });
      }
      setLoading(false);
      closeModal();
      alert.success(t('Receipt has been sent'));
    } catch (err: any) {
      setLoading(false);
      closeModal();
      alert.error(t('Error sending receipt'));
    }
  };

  return (
    <Modal.Body
      css={css`
        overflow: visible;
      `}
    >
      <ListField
        name='receipt-email-list'
        label={t('Add an email')}
        helperText={t('Note: Only 3 email addressees may be added.')}
        {...fieldProps}
        fieldType='email'
      />
      <Modal.Footer
        css={css`
          padding: 0;
        `}
        primary={{
          type: 'button',
          onClick: sendReceipts,
          disabled: !fieldProps.value.length,
          label: t('Send'),
        }}
        secondary={{
          onClick: closeModal,
          label: t('Cancel'),
        }}
      />
    </Modal.Body>
  );
};
